import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, Avatar, Tooltip, Chip } from '@mui/material';
import { Dashboard, People, CalendarMonth, Inventory2, Receipt, LocalHospital, Person, Logout, MonitorHeart } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const DRAWER_WIDTH = 255;

const navItems = [
  { label: 'Dashboard', path: '/dashboard', icon: <Dashboard fontSize="small" /> },
  { label: 'Hospitals', path: '/hospitals', icon: <LocalHospital fontSize="small" />, roles: ['superadmin'] },
  { label: 'Patients', path: '/patients', icon: <People fontSize="small" /> },
  { label: 'Appointments', path: '/appointments', icon: <CalendarMonth fontSize="small" /> },
  { label: 'Inventory', path: '/inventory', icon: <Inventory2 fontSize="small" /> },
  { label: 'Billing', path: '/billing', icon: <Receipt fontSize="small" /> },
  { label: 'Staff', path: '/staff', icon: <Person fontSize="small" />, roles: ['superadmin', 'admin'] },
];

const roleColors: Record<string, string> = {
  superadmin: '#f59e0b', admin: '#3b82f6', doctor: '#10b981',
  nurse: '#8b5cf6', receptionist: '#ec4899', pharmacist: '#06b6d4', accountant: '#f97316'
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const filteredNav = navItems.filter(item => !item.roles || item.roles.includes(user?.role || ''));

  return (
    <Box sx={{
      width: DRAWER_WIDTH, flexShrink: 0, height: '100vh', position: 'fixed',
      background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
      display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(255,255,255,0.05)'
    }}>
      {/* Logo */}
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ bgcolor: '#3b82f6', p: 1, borderRadius: 2, display: 'flex' }}>
          <MonitorHeart sx={{ color: 'white', fontSize: 22 }} />
        </Box>
        <Box>
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 17, lineHeight: 1 }}>MedelaHub</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 11, mt: 0.3 }}>HMS Platform</Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      {/* User Profile */}
      <Box sx={{ px: 2, py: 2 }}>
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, border: '1px solid rgba(255,255,255,0.08)' }}>
          <Avatar sx={{ width: 38, height: 38, bgcolor: roleColors[user?.role || ''] || '#3b82f6', fontSize: 15, fontWeight: 700 }}>
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: 'white', fontSize: 13, fontWeight: 600 }} noWrap>{user?.name}</Typography>
            <Typography sx={{ color: '#64748b', fontSize: 11, textTransform: 'capitalize' }}>{user?.role}</Typography>
          </Box>
        </Box>
        {user?.hospital && (
          <Typography sx={{ color: '#475569', fontSize: 11, mt: 1, px: 0.5 }} noWrap>
            🏥 {user.hospital.name}
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2 }} />

      {/* Navigation */}
      <Typography sx={{ color: '#475569', fontSize: 10, fontWeight: 700, letterSpacing: 1, px: 3, pt: 2, pb: 1, textTransform: 'uppercase' }}>
        Main Menu
      </Typography>

      <List sx={{ flex: 1, px: 1.5, py: 0 }}>
        {filteredNav.map(item => {
          const active = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2, py: 1.1, px: 1.5,
                  bgcolor: active ? 'rgba(59,130,246,0.15)' : 'transparent',
                  border: active ? '1px solid rgba(59,130,246,0.25)' : '1px solid transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' },
                  transition: 'all 0.15s ease'
                }}
              >
                <ListItemIcon sx={{ minWidth: 34, color: active ? '#60a5fa' : '#475569' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 13.5, fontWeight: active ? 600 : 400, color: active ? '#e2e8f0' : '#94a3b8' }}
                />
                {active && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#3b82f6' }} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2 }} />

      {/* Logout */}
      <List sx={{ px: 1.5, py: 1.5 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={logout}
            sx={{ borderRadius: 2, py: 1, '&:hover': { bgcolor: 'rgba(239,68,68,0.1)', '& .logout-icon': { color: '#ef4444' } } }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>
              <Logout className="logout-icon" sx={{ color: '#475569', fontSize: 18, transition: 'color 0.15s' }} />
            </ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 13, color: '#64748b' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
};

export { DRAWER_WIDTH };
export default Sidebar;
